<template>
  <main>
    <header class="hero">
      <div>
        <p class="eyebrow">Hitmarker historical jobs report</p>
        <h1>
          Market movement across jobs, categories, geography, salary, and ATS
          usage.
        </h1>
        <p class="hero-copy">
          Canonical job posts from {{ firstMonthLabel }} to
          {{ lastMonthLabel }}. Salary charts are sample-based and always paired
          with coverage.
        </p>
      </div>
      <div class="hero-meta">
        <span>Generated {{ generatedAt }}</span>
        <span>{{ formatNumber(allJobs.length) }} jobs loaded</span>
      </div>
    </header>

    <section v-if="loading" class="panel state-panel">
      Loading report data...
    </section>
    <section v-else-if="error" class="panel state-panel error-panel">
      <h2>Dataset unavailable</h2>
      <p>{{ error }}</p>
      <p>Run <code>npm run generate:data</code>, then refresh this page.</p>
    </section>

    <template v-else>
      <FilterBar
        :filters="filters"
        :min-month="months[0]"
        :max-month="months[months.length - 1]"
        :levels="levelOptions"
        :contracts="contractOptions"
        :parent-categories="parentCategoryOptions"
        :child-tags="childTagOptions"
        :geos="geoOptions"
        :providers="providerOptions"
        :hosts="hostOptions"
        @reset="resetFilters"
      />

      <section class="kpi-grid">
        <article class="kpi-card">
          <span>Matching jobs</span>
          <strong>{{ formatNumber(filteredJobs.length) }}</strong>
          <small
            >{{
              formatPercent(filteredJobs.length / Math.max(allJobs.length, 1))
            }}
            of loaded jobs</small
          >
        </article>
        <article class="kpi-card">
          <span>Selected-period change</span>
          <strong :class="periodChange.delta >= 0 ? 'positive' : 'negative'">
            {{ periodChange.delta >= 0 ? "+" : ""
            }}{{ formatNumber(periodChange.delta) }}
          </strong>
          <small>vs previous equivalent period</small>
        </article>
        <article class="kpi-card">
          <span>Salary coverage</span>
          <strong>{{ formatPercent(salary.presentRate) }}</strong>
          <small
            >{{ formatNumber(salary.present) }} present,
            {{ formatNumber(salary.parsed) }} parsed</small
          >
        </article>
        <article class="kpi-card">
          <span>Top ATS provider</span>
          <strong>{{ topProvider?.title || "N/A" }}</strong>
          <small>{{
            topProvider
              ? formatNumber(topProvider.count) + " matching jobs"
              : "No provider data"
          }}</small>
        </article>
        <article class="kpi-card">
          <span>Top hiring company</span>
          <strong>{{ topCompany?.title || "N/A" }}</strong>
          <small v-if="topCompany"
            >{{ formatNumber(topCompany.count) }} matching jobs,
            {{ topCompany.firstMonth }} to {{ topCompany.lastMonth }}</small
          >
          <small v-else>No company data</small>
        </article>
      </section>

      <section class="grid two-col">
        <ChartCard
          title="Monthly postings"
          subtitle="Post date drives the timeline. Filters apply to the whole series."
          type="line"
          :data="monthlyChart"
          :options="lineOptions"
        />
        <ChartCard
          title="Application type"
          subtitle="URL vs email based on the stored application type."
          type="doughnut"
          :data="applicationTypeChart"
        />
      </section>

      <section class="grid two-col">
        <MovementTable title="Category movement" :rows="categoryGrowth" />
        <MovementTable title="Geography movement" :rows="geoGrowth" />
      </section>

      <section class="company-panel">
        <MovementTable title="ATS provider movement" :rows="providerGrowth" />
      </section>

      <section class="grid two-col">
        <MovementTable title="Job level movement" :rows="levelGrowth" />
        <MovementTable title="Contract movement" :rows="contractGrowth" />
      </section>

      <section class="grid two-col">
        <MovementTable
          title="Company movement"
          subtitle="Companies gaining or losing posting volume and market share in the comparison window."
          :rows="companyGrowth"
        />
        <ChartCard
          title="Top hiring companies"
          subtitle="Companies ranked by matching job posts in the current filter set."
          :data="companyChart"
        />
      </section>

      <section class="panel company-panel">
        <div class="panel-header">
          <div>
            <h2>Big hirers</h2>
            <p>
              Company-level rollup for the current filters, including first and
              last matching job month.
            </p>
          </div>
        </div>
        <div class="table-scroll">
          <table class="company-table">
            <thead>
              <tr>
                <th>Company</th>
                <th>Jobs</th>
                <th>Share</th>
                <th>First job</th>
                <th>Last job</th>
                <th class="text-column">Top category</th>
                <th class="text-column">Top location</th>
                <th class="text-column">Top source</th>
                <th class="text-column">Top ATS</th>
                <th>Salary coverage</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in topCompanies.slice(0, 30)" :key="row.id">
                <td>{{ row.title }}</td>
                <td>{{ formatNumber(row.count) }}</td>
                <td>{{ formatPercent(row.share) }}</td>
                <td>{{ row.firstMonth || "N/A" }}</td>
                <td>{{ row.lastMonth || "N/A" }}</td>
                <td class="text-column">
                  {{ formatDimensionCell(row.topCategory) }}
                </td>
                <td class="text-column">
                  {{ formatDimensionCell(row.topGeo) }}
                </td>
                <td class="text-column">
                  {{ formatDimensionCell(row.topSource) }}
                </td>
                <td class="text-column">
                  {{ formatDimensionCell(row.topProvider) }}
                </td>
                <td>{{ formatPercent(row.salaryRate) }}</td>
              </tr>
              <tr v-if="topCompanies.length === 0">
                <td colspan="10">
                  No company data for the current filter set.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section class="grid two-col">
        <ChartCard
          title="Top parent categories"
          subtitle="Top-level tag categories keep the main view readable."
          :data="parentCategoryChart"
        />
        <ChartCard
          title="Top child tags"
          subtitle="Detailed job tags for the current filter set."
          :data="childTagChart"
        />
      </section>

      <section class="grid two-col">
        <ChartCard
          title="Job level mix"
          subtitle="Seniority bands from the stored job level field."
          :data="levelChart"
        />
        <ChartCard
          title="Contract mix"
          subtitle="Contract checkboxes can contain multiple values, so shares can overlap."
          :data="contractChart"
        />
      </section>

      <section class="panel company-panel">
        <div class="panel-header">
          <div>
            <h2>Top countries and remote buckets</h2>
            <p>Multi-location jobs count once in each matching bucket.</p>
          </div>
        </div>
        <table>
          <thead>
            <tr>
              <th>Location</th>
              <th>Jobs</th>
              <th>Share</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in topGeos.slice(0, 18)" :key="row.id">
              <td>{{ row.title }}</td>
              <td>{{ formatNumber(row.count) }}</td>
              <td>
                {{ formatPercent(row.count / Math.max(filteredJobs.length, 1)) }}
              </td>
            </tr>
          </tbody>
        </table>
      </section>

      <section class="grid two-col">
        <section class="panel">
          <div class="panel-header">
            <div>
              <h2>Salary segments</h2>
              <p>
                Segmented by currency and period. No cross-currency conversion
                is performed.
              </p>
            </div>
          </div>
          <table>
            <thead>
              <tr>
                <th>Segment</th>
                <th>Parsed jobs</th>
                <th>Median midpoint</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="segment in salary.segments.slice(0, 12)"
                :key="segment.key"
              >
                <td>{{ segment.currency }} / {{ segment.period }}</td>
                <td>{{ formatNumber(segment.count) }}</td>
                <td>
                  {{
                    segment.median
                      ? formatMoney(segment.median, segment.currency)
                      : "N/A"
                  }}
                </td>
              </tr>
              <tr v-if="salary.segments.length === 0">
                <td colspan="3">
                  No parsed salary data for the current filter set.
                </td>
              </tr>
            </tbody>
          </table>
        </section>
        <ChartCard
          title="Salary coverage by year"
          subtitle="Coverage is shown separately from salary values to avoid overstating incomplete data."
          type="line"
          :data="coverageChart"
          :options="percentOptions"
        />
      </section>

      <section class="grid two-col">
        <section class="panel">
          <div class="panel-header">
            <div>
              <h2>Top ATS providers</h2>
              <p>Popularity means posting count, not apply clicks.</p>
            </div>
          </div>
          <table>
            <thead>
              <tr>
                <th>Provider</th>
                <th>Jobs</th>
                <th>Share</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in topProviders.slice(0, 15)" :key="row.id">
                <td>{{ row.title }}</td>
                <td>{{ formatNumber(row.count) }}</td>
                <td>
                  {{
                    formatPercent(row.count / Math.max(filteredJobs.length, 1))
                  }}
                </td>
              </tr>
            </tbody>
          </table>
        </section>
        <section class="panel">
          <div class="panel-header">
            <div>
              <h2>Top raw hosts</h2>
              <p>
                Hostnames are kept for ATS drilldown; raw URLs are never
                shipped.
              </p>
            </div>
          </div>
          <table>
            <thead>
              <tr>
                <th>Host</th>
                <th>Provider</th>
                <th>Jobs</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in topHosts.slice(0, 15)" :key="row.id">
                <td>{{ row.title }}</td>
                <td>{{ row.item?.provider || row.title }}</td>
                <td>{{ formatNumber(row.count) }}</td>
              </tr>
            </tbody>
          </table>
        </section>
      </section>

      <section class="panel">
        <div class="panel-header">
          <div>
            <h2>Data quality by year</h2>
            <p>Use this to read every trend with the right confidence.</p>
          </div>
        </div>
        <div class="table-scroll">
          <table class="quality-table">
            <thead>
              <tr>
                <th>Year</th>
                <th>Jobs</th>
                <th>Salary present</th>
                <th>Salary parsed</th>
                <th>Tags present</th>
                <th>Level present</th>
                <th>Contract present</th>
                <th>Location present</th>
                <th>Provider present</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in coverageRows" :key="row.year">
                <td>{{ row.year }}</td>
                <td>{{ formatNumber(row.jobs) }}</td>
                <td>{{ formatPercent(row.salaryPresent / row.jobs) }}</td>
                <td>{{ formatPercent(row.salaryParsed / row.jobs) }}</td>
                <td>{{ formatPercent(row.tagsPresent / row.jobs) }}</td>
                <td>{{ formatPercent(row.levelPresent / row.jobs) }}</td>
                <td>{{ formatPercent(row.contractPresent / row.jobs) }}</td>
                <td>{{ formatPercent(row.geoPresent / row.jobs) }}</td>
                <td>{{ formatPercent(row.providerPresent / row.jobs) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </template>
  </main>
</template>

<script setup>
import { computed, markRaw, reactive, ref } from "vue";
import ChartCard from "./components/ChartCard.vue";
import FilterBar from "./components/FilterBar.vue";
import MovementTable from "./components/MovementTable.vue";
import {
  allMonths,
  applyFilters,
  companyRows,
  coverageByYear,
  createDefaultFilters,
  dimensionCounts,
  growthTable,
  monthSeries,
  selectedPeriodChange,
} from "./lib/analytics.js";
import { reportBasePath } from "./lib/reportPath.js";

const loading = ref(true);
const error = ref("");
const meta = ref(null);
const dictionaries = ref({
  companies: {},
  parentCategories: {},
  childTags: {},
  contracts: {},
  levels: {},
  geos: {},
  cities: {},
  providers: {},
  hosts: {},
  sources: {},
});
const allJobs = ref([]);
const filters = reactive(createDefaultFilters([]));

loadData();

const months = computed(() =>
  meta.value?.months?.length ? meta.value.months : allMonths(allJobs.value),
);
const firstMonthLabel = computed(() => months.value[0] || "N/A");
const lastMonthLabel = computed(
  () => months.value[months.value.length - 1] || "N/A",
);
const generatedAt = computed(() =>
  meta.value?.generatedAt
    ? new Date(meta.value.generatedAt).toLocaleString()
    : "N/A",
);

const filteredJobs = computed(() =>
  applyFilters(allJobs.value, filters, dictionaries.value),
);
const movementJobs = computed(() => {
  const next = { ...filters, startMonth: "", endMonth: "" };
  return applyFilters(allJobs.value, next, dictionaries.value);
});
const selectedMonths = computed(() =>
  months.value.filter(
    (month) =>
      (!filters.startMonth || month >= filters.startMonth) &&
      (!filters.endMonth || month <= filters.endMonth),
  ),
);
const monthly = computed(() =>
  monthSeries(filteredJobs.value, selectedMonths.value),
);
const periodChange = computed(() =>
  selectedPeriodChange(
    movementJobs.value,
    filters.startMonth,
    filters.endMonth,
  ),
);
const salary = computed(() => salarySummaryLocal(filteredJobs.value));
const coverageRows = computed(() => coverageByYear(filteredJobs.value));

const topProviders = computed(() =>
  dimensionCounts(
    filteredJobs.value,
    "pr",
    dictionaries.value.providers,
  ).filter((row) => row.id !== "None"),
);
const topProvider = computed(() => topProviders.value[0] || null);
const topHosts = computed(() =>
  dimensionCounts(filteredJobs.value, "h", dictionaries.value.hosts).filter(
    (row) => row.id,
  ),
);
const topGeos = computed(() =>
  dimensionCounts(filteredJobs.value, "g", dictionaries.value.geos).filter(
    (row) => row.id !== "unknown:unknown",
  ),
);
const topCompanies = computed(() =>
  companyRows(filteredJobs.value, dictionaries.value),
);
const topCompany = computed(() => topCompanies.value[0] || null);

const categoryGrowth = computed(() =>
  growthTable(
    movementJobs.value,
    "p",
    dictionaries.value.parentCategories,
    filters.endMonth,
  ),
);
const geoGrowth = computed(() =>
  growthTable(
    movementJobs.value,
    "g",
    dictionaries.value.geos,
    filters.endMonth,
  ),
);
const providerGrowth = computed(() =>
  growthTable(
    movementJobs.value,
    "pr",
    dictionaries.value.providers,
    filters.endMonth,
  ),
);
const companyGrowth = computed(() =>
  growthTable(
    movementJobs.value,
    "c",
    dictionaries.value.companies,
    filters.endMonth,
  ),
);
const levelGrowth = computed(() =>
  growthTable(
    movementJobs.value,
    "lv",
    dictionaries.value.levels,
    filters.endMonth,
  ).filter((row) => row.id !== "unknown"),
);
const contractGrowth = computed(() =>
  growthTable(
    movementJobs.value,
    "ct",
    dictionaries.value.contracts,
    filters.endMonth,
  ).filter((row) => row.id !== "unknown"),
);

const levelOptions = computed(() =>
  toOptions(dictionaries.value.levels).filter(
    (option) => option.id !== "unknown",
  ),
);
const contractOptions = computed(() =>
  toOptions(dictionaries.value.contracts).filter(
    (option) => option.id !== "unknown",
  ),
);
const parentCategoryOptions = computed(() =>
  toOptions(dictionaries.value.parentCategories),
);
const childTagOptions = computed(() => toOptions(dictionaries.value.childTags));
const geoOptions = computed(() =>
  toOptions(dictionaries.value.geos).filter(
    (option) => option.id !== "unknown:unknown",
  ),
);
const providerOptions = computed(() =>
  toOptions(dictionaries.value.providers).filter(
    (option) => option.id !== "None",
  ),
);
const hostOptions = computed(() =>
  topHosts.value.slice(0, 200).map((row) => ({ id: row.id, title: row.title })),
);

const monthlyChart = computed(() => ({
  labels: monthly.value.labels,
  datasets: [
    {
      label: "Jobs",
      data: monthly.value.values,
      borderColor: "#ff5a1f",
      backgroundColor: "rgba(255, 90, 31, 0.12)",
      pointRadius: 0,
      tension: 0.28,
      fill: true,
    },
  ],
}));

const applicationTypeChart = computed(() =>
  chartFromCounts(
    dimensionCounts(filteredJobs.value, "a", {
      url: { title: "URL" },
      email: { title: "Email" },
      unknown: { title: "Unknown" },
    }).slice(0, 5),
  ),
);

const parentCategoryChart = computed(() =>
  chartFromCounts(
    dimensionCounts(
      filteredJobs.value,
      "p",
      dictionaries.value.parentCategories,
    ).slice(0, 10),
  ),
);
const childTagChart = computed(() =>
  chartFromCounts(
    dimensionCounts(
      filteredJobs.value,
      "t",
      dictionaries.value.childTags,
    ).slice(0, 10),
  ),
);
const levelChart = computed(() =>
  chartFromCounts(
    dimensionCounts(filteredJobs.value, "lv", dictionaries.value.levels)
      .filter((row) => row.id !== "unknown")
      .slice(0, 10),
  ),
);
const contractChart = computed(() =>
  chartFromCounts(
    dimensionCounts(filteredJobs.value, "ct", dictionaries.value.contracts)
      .filter((row) => row.id !== "unknown")
      .slice(0, 10),
  ),
);
const companyChart = computed(() =>
  chartFromCounts(topCompanies.value.slice(0, 10)),
);

const coverageChart = computed(() => ({
  labels: coverageRows.value.map((row) => row.year),
  datasets: [
    {
      label: "Salary present",
      data: coverageRows.value.map(
        (row) => Math.round((row.salaryPresent / row.jobs) * 1000) / 10,
      ),
      borderColor: "#ff5a1f",
      backgroundColor: "rgba(255, 90, 31, 0.1)",
      tension: 0.2,
    },
    {
      label: "Salary parsed",
      data: coverageRows.value.map(
        (row) => Math.round((row.salaryParsed / row.jobs) * 1000) / 10,
      ),
      borderColor: "#4f46e5",
      backgroundColor: "rgba(79, 70, 229, 0.1)",
      tension: 0.2,
    },
  ],
}));

const lineOptions = {
  plugins: { legend: { display: false } },
};

const percentOptions = {
  scales: {
    y: {
      beginAtZero: true,
      max: 100,
      ticks: {
        callback: (value) => `${value}%`,
      },
    },
  },
};

async function loadData() {
  loading.value = true;
  error.value = "";
  try {
    const base = reportBasePath();
    const metaResponse = await fetch(`${base}/data/meta.json`);
    if (!metaResponse.ok)
      throw new Error(`Could not load meta.json (${metaResponse.status})`);
    meta.value = await metaResponse.json();

    const payload = await fetchPayload(base);
    dictionaries.value = markRaw(payload.dictionaries);
    allJobs.value = markRaw(payload.jobs);
    resetFilters();
  } catch (err) {
    error.value = err instanceof Error ? err.message : String(err);
  } finally {
    loading.value = false;
  }
}

async function fetchPayload(base) {
  try {
    const response = await fetch(`${base}/data/jobs.json.br`);
    if (!response.ok) throw new Error("No compressed dataset");
    return await response.json();
  } catch {
    const response = await fetch(`${base}/data/jobs.json`);
    if (!response.ok)
      throw new Error(`Could not load jobs dataset (${response.status})`);
    return response.json();
  }
}

function resetFilters() {
  Object.assign(filters, createDefaultFilters(months.value));
}

function chartFromCounts(rows) {
  return {
    labels: rows.map((row) => row.title),
    datasets: [
      {
        label: "Jobs",
        data: rows.map((row) => row.count),
        backgroundColor: [
          "#ff5a1f",
          "#172033",
          "#4f46e5",
          "#00866f",
          "#f2b705",
          "#7b61ff",
          "#00a3ff",
          "#cc3a1f",
          "#6b7280",
          "#111827",
        ],
      },
    ],
  };
}

function salarySummaryLocal(jobs) {
  const total = jobs.length;
  const present = jobs.filter((job) => job.sal?.present).length;
  const parsed = jobs.filter((job) => job.sal?.parsed).length;
  const groups = new Map();

  for (const job of jobs) {
    const sal = job.sal;
    if (!sal?.parsed) continue;
    const key = `${sal.currency}-${sal.period}`;
    const group = groups.get(key) || {
      key,
      currency: sal.currency,
      period: sal.period,
      count: 0,
      midpoints: [],
    };
    group.count += 1;
    if (Number.isFinite(sal.midpoint)) group.midpoints.push(sal.midpoint);
    groups.set(key, group);
  }

  return {
    total,
    present,
    parsed,
    presentRate: total ? present / total : 0,
    parsedRate: total ? parsed / total : 0,
    segments: [...groups.values()]
      .map((group) => ({ ...group, median: median(group.midpoints) }))
      .sort((a, b) => b.count - a.count),
  };
}

function toOptions(dictionary) {
  return Object.values(dictionary || {}).sort((a, b) =>
    String(a.title).localeCompare(String(b.title)),
  );
}

function formatNumber(value) {
  return new Intl.NumberFormat("en").format(value || 0);
}

function formatPercent(value) {
  if (!Number.isFinite(value)) return "N/A";
  return `${(value * 100).toFixed(value > 0 && value < 0.01 ? 2 : 1)}%`;
}

function formatMoney(value, currency) {
  return new Intl.NumberFormat("en", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(value);
}

function formatDimensionCell(dimension) {
  return dimension
    ? `${dimension.title} (${formatNumber(dimension.count)})`
    : "N/A";
}

function median(values) {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? Math.round((sorted[middle - 1] + sorted[middle]) / 2)
    : sorted[middle];
}
</script>
